// frontend/src/components/Products/ProductsList.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { FiPlus, FiEdit2, FiImage } from 'react-icons/fi';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import './ProductList.css';

const ProductList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({ productCode: '', productName: '', status: '' });

  useEffect(() => { loadProducts(); }, [currentPage, itemsPerPage, filters]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const q = new URLSearchParams({
        page: currentPage, limit: itemsPerPage,
        ...(filters.productCode && { productCode: filters.productCode }),
        ...(filters.productName && { productName: filters.productName }),
        ...(filters.status      && { status:      filters.status }),
      });
      const res = await productsAPI.getAll(`?${q.toString()}`);
      if (res.data.success) {
        setProducts(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
        setTotalItems(res.data.pagination.total);
      }
      setError(null);
    } catch (err) {
      setError(t('messages.error_load', 'Failed to load products'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const getImageUrl = (filePath) =>
    filePath
      ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/attachments/view/${encodeURIComponent(filePath)}`
      : null;

  const statusClass = (s) => ({
    'ACTIVE': 'status-active', 'DISCONTINUED': 'status-discontinued', 'DRAFT': 'status-draft'
  }[s] || 'status-draft');

  const columns = [
    {
      header: t('products.image', 'Image'), field: 'base_image_path', sortable: false,
      render: (row) => (
        <div className="product-thumbnail">
          {row.base_image_path
            ? <img src={getImageUrl(row.base_image_path)} alt={row.product_name}
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
            : null}
          <div className="no-image" style={{ display: row.base_image_path ? 'none' : 'flex' }}>
            <FiImage size={24} />
          </div>
        </div>
      )
    },
    { header: t('products.code', 'Product Code'), field: 'product_code', className: 'code-column' },
    { header: t('products.name', 'Name'),          field: 'product_name' },
    { header: t('products.category', 'Category'),  field: 'category' },
    {
      header: t('products.status', 'Status'), field: 'status', sortable: false,
      render: (row) => (
        <span className={`status-badge ${statusClass(row.status)}`}>
          {t(`status.${row.status?.toLowerCase()}`, row.status)}
        </span>
      )
    },
    {
      header: t('actions', 'Actions'), field: 'actions', sortable: false,
      render: (row) => (
        <div className="action-buttons">
          <button onClick={() => navigate(`/products/${row.public_id}`)}
            className="btn-action btn-edit" title={t('edit', 'Edit')}>
            <FiEdit2 size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="product-list">
      <div className="page-header">
        <div>
          <h2>{t('products.title', 'Products')}</h2>
          <p className="page-subtitle">{t('products.subtitle', 'Manage product catalog')}</p>
        </div>
        <button onClick={() => navigate('/products/new')} className="btn-primary">
          <FiPlus size={16} style={{ marginRight: '6px' }} />
          {t('products.create', 'Create Product')}
        </button>
      </div>

      <div className="product-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>{t('products.code', 'Product Code')}</label>
            <input type="text" value={filters.productCode} className="form-control-sm"
              placeholder={t('search', 'Search...')}
              onChange={e => handleFilterChange('productCode', e.target.value)} />
          </div>
          <div className="filter-group">
            <label>{t('products.name', 'Name')}</label>
            <input type="text" value={filters.productName} className="form-control-sm"
              placeholder={t('search', 'Search...')}
              onChange={e => handleFilterChange('productName', e.target.value)} />
          </div>
          <div className="filter-group">
            <label>{t('products.status', 'Status')}</label>
            <select value={filters.status} className="form-control-sm"
              onChange={e => handleFilterChange('status', e.target.value)}>
              <option value="">{t('all', 'All')}</option>
              <option value="ACTIVE">{t('status.active', 'Active')}</option>
              <option value="DRAFT">{t('status.draft', 'Draft')}</option>
              <option value="DISCONTINUED">{t('status.discontinued', 'Discontinued')}</option>
            </select>
          </div>
        </div>
      </div>

      <DataTable columns={columns} data={products} loading={loading} error={error}
        onRetry={loadProducts}
        onRowClick={(row) => navigate(`/products/${row.public_id}`)}
        emptyMessage={t('no_data', 'No products found')} />

      {totalPages > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages}
          totalItems={totalItems} itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }} />
      )}
    </div>
  );
};

export default ProductList;